import React, { useState } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import Link from 'next/link';

import { useIntl } from '@klokhuis/core/Intl';
import { PageLayout } from '@klokhuis/core/PageLayout';

import { INSIGHT_ARTICLES } from '../articles';
import styles from './RealEstateInsightsContent.module.css';

const UNIQUE_CATEGORIES = Array.from(new Set(INSIGHT_ARTICLES.map(a => a.category)));
const UNIQUE_AUDIENCES = Array.from(new Set(INSIGHT_ARTICLES.map(a => a.audience)));

const ALL_KEY = '__ALL__';

export const RealEstateInsightsContent: React.FC = () => {
  const { T } = useIntl();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(ALL_KEY);
  const [activeAudience, setActiveAudience] = useState(ALL_KEY);

  const filtered = INSIGHT_ARTICLES.filter(a => {
    const matchCat = activeCategory === ALL_KEY || a.category === activeCategory;
    const matchAud = activeAudience === ALL_KEY || a.audience === activeAudience;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.tags.some(t => t.toLowerCase().includes(q));
    return matchCat && matchAud && matchSearch;
  });

  return (
    <PageLayout.SectionContainer className={styles.root}>
      {/* Hero */}
      <Box className={styles.hero}>
        <Box className={styles.heroBadge}>{T('insights.hero.badge')}</Box>
        <Typography variant="h3" className={styles.heroTitle}>
          {T('insights.hero.title')}
        </Typography>
        <Typography variant="body1" className={styles.heroSubtitle}>
          {T('insights.hero.subtitle')}
        </Typography>
        <TextField
          id="insights-search"
          fullWidth
          placeholder={T('insights.search.placeholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={styles.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Stats */}
      <Box className={styles.statsBar}>
        <Box className={styles.statItem}>
          <Typography className={styles.statNumber}>{INSIGHT_ARTICLES.length}</Typography>
          <Typography className={styles.statLabel}>{T('insights.stats.expert_guides')}</Typography>
        </Box>
        <Box className={styles.statItem}>
          <Typography className={styles.statNumber}>5</Typography>
          <Typography className={styles.statLabel}>{T('insights.stats.states_covered')}</Typography>
        </Box>
        <Box className={styles.statItem}>
          <Typography className={styles.statNumber}>Free</Typography>
          <Typography className={styles.statLabel}>{T('insights.stats.always_free')}</Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Box className={styles.filterSection}>
        <Box className={styles.filterRow}>
          <Box className={styles.filterLabel}>
            <FilterListIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">{T('insights.filter.category')}</Typography>
          </Box>
          <Box className={styles.chips}>
            <Chip
              key={ALL_KEY}
              label={T('insights.filter.all')}
              clickable
              size="small"
              onClick={() => setActiveCategory(ALL_KEY)}
              color={activeCategory === ALL_KEY ? 'primary' : 'default'}
              variant={activeCategory === ALL_KEY ? 'filled' : 'outlined'}
            />
            {UNIQUE_CATEGORIES.map(cat => (
              <Chip
                key={cat}
                label={cat}
                clickable
                size="small"
                onClick={() => setActiveCategory(cat)}
                color={activeCategory === cat ? 'primary' : 'default'}
                variant={activeCategory === cat ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>
        <Box className={styles.filterRow}>
          <Box className={styles.filterLabel}>
            <FilterListIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">{T('insights.filter.for')}</Typography>
          </Box>
          <Box className={styles.chips}>
            <Chip
              key={ALL_KEY}
              label={T('insights.filter.all')}
              clickable
              size="small"
              onClick={() => setActiveAudience(ALL_KEY)}
              color={activeAudience === ALL_KEY ? 'secondary' : 'default'}
              variant={activeAudience === ALL_KEY ? 'filled' : 'outlined'}
            />
            {UNIQUE_AUDIENCES.map(aud => (
              <Chip
                key={aud}
                label={aud}
                clickable
                size="small"
                onClick={() => setActiveAudience(aud)}
                color={activeAudience === aud ? 'secondary' : 'default'}
                variant={activeAudience === aud ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Results count */}
      <Typography variant="body2" className={styles.resultCount}>
        {T('insights.results.showing', filtered.length, INSIGHT_ARTICLES.length)}
      </Typography>

      {/* Grid — links to individual pages */}
      {filtered.length > 0 ? (
        <Box className={styles.grid}>
          {filtered.map(article => (
            <Link
              key={article.id}
              href={`/real-estate-insights/${article.slug}`}
              passHref
              legacyBehavior
            >
              <Box component="a" className={styles.card}>
                <Box className={styles.cardTop}>
                  <Box className={styles.cardIconWrap}>{article.icon}</Box>
                  <Box className={styles.cardBadges}>
                    <Chip
                      label={article.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                      className={styles.categoryBadge}
                    />
                    <Chip
                      label={article.audience}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      className={styles.audienceBadge}
                    />
                  </Box>
                </Box>
                <Typography variant="subtitle1" className={styles.cardTitle}>
                  {article.title}
                </Typography>
                <Typography variant="body2" className={styles.cardSummary}>
                  {article.summary}
                </Typography>
                <Box className={styles.cardFooter}>
                  <Typography variant="caption" className={styles.readTime}>
                    {article.readTime}
                  </Typography>
                  <Box className={styles.readMore}>
                    <Typography variant="caption">{T('insights.card.read_guide')}</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Box>
              </Box>
            </Link>
          ))}
        </Box>
      ) : (
        <Box className={styles.empty}>
          <Typography color="text.secondary">
            {T('insights.empty.message')}
          </Typography>
        </Box>
      )}

      {/* Disclaimer */}
      <Box className={styles.disclaimer}>
        <Typography variant="caption" color="text.secondary">
          {T('insights.disclaimer')}
        </Typography>
      </Box>
    </PageLayout.SectionContainer>
  );
};
